import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import { useEffect, useSyncExternalStore } from 'react'

import { useTenantMemberships } from '@/hooks/useTenantMemberships'
import {
  initAnalytics,
  isAnalyticsConfigured,
  isAnalyticsOptedOut,
  resolveRoutePattern,
  subscribeToAnalyticsOptOut,
  trackPageView,
} from '@/lib/analytics'
import { useTenant } from '@/lib/tenant'

export function useAnalyticsOptOut(): boolean {
  return useSyncExternalStore(subscribeToAnalyticsOptOut, isAnalyticsOptedOut)
}

export function useAnalyticsBootstrap(): void {
  const router = useRouter()
  const { tenantSlug } = useTenant()
  const { data: membershipsResult } = useTenantMemberships()
  const isOptedOut = useAnalyticsOptOut()

  const membership = membershipsResult?.data?.find(
    (tenantMembership) => tenantMembership.slug === tenantSlug,
  )
  const tenantId = membership?.tenantId
  const userRole = membership?.role

  useEffect(() => {
    if (!isAnalyticsConfigured() || !tenantId || !userRole || isOptedOut) return

    let isCancelled = false
    initAnalytics({ tenantId, userRole })
      .then((hasLoaded) => {
        if (hasLoaded && !isCancelled) {
          trackPageView(resolveRoutePattern(router.state.matches))
        }
      })
      .catch((caughtError: unknown) => Sentry.captureException(caughtError))
    return () => {
      isCancelled = true
    }
  }, [tenantId, userRole, isOptedOut, router])
}
